import { useState, useEffect, useRef, useMemo, useCallback, startTransition } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  Tabs,
  Tab,
  TextField,
} from "@mui/material";
import CustomTable from "../../components/CustomTable/CustomTable";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import {
  getSegregateTrades,
  getSegregateTradesColumns,
  uploadTradesFile,
  getStrategyTransactions,
  getStrategyTransactionsColumns,
  getSegregatedStrategies,
  createStrategy,
} from "../../api/strategyProfiling";
import { extractColumnsConfig } from "../../utils/brokerBookColumns";
import BatchManagementTab from "../../components/BatchManagementTab/BatchManagementTab";
import SegregateTradesModal from "../../components/SegregateTradesModal/SegregateTradesModal";
import styles from "./StrategyProfilingPage.module.css";
import SummaryPerformanceChart from "./SummaryPerformanceChart";

const getErrorMessage = (err) => err?.response?.data?.detail || err.message || "An error occurred";

const normalizeApiData = (response) => {
  return response?.data || response?.trades || response?.transactions || response || [];
};

const buildFilters = (dateFilter, strategyFilter) => ({
  date_filter: dateFilter,
  strategy_filter: strategyFilter === "all" ? null : strategyFilter,
});

const formatCurrency = (amount) => {
  const absAmount = Math.abs(amount);
  const parts = absAmount.toFixed(3).split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `₹ ${integerPart}.${parts[1]}`;
};

const DATE_FILTER_OPTIONS = [
  { label: "Last Month", value: "last_month" },
  { label: "Last Week", value: "last_week" },
  { label: "Last 3 Months", value: "last_3_months" },
  { label: "Last 6 Months", value: "last_6_months" },
  { label: "All Time", value: "all" },
];

const COLUMNS_WITH_DROPDOWN = ["broker_code", "trading_symbol", "exchange"];

const STRATEGY_ICON_COLORS = ["#2DD4BF", "#FB7185", "#60A5FA", "#FBBF24", "#A78BFA", "#F472B6"];

function StrategyProfilingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const primaryTabFromUrl = searchParams.get("primaryTab") || "my_strategies";
  const [primaryTab, setPrimaryTab] = useState(primaryTabFromUrl);
  const [hasVisitedBatchTab, setHasVisitedBatchTab] = useState(primaryTabFromUrl === "batch_management");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [strategyTransactions, setStrategyTransactions] = useState({});
  const [strategyTransactionColumns, setStrategyTransactionColumns] = useState({});
  const [strategyDateFilter, setStrategyDateFilter] = useState({});
  const [strategyTransactionLoading, setStrategyTransactionLoading] = useState({});
  const [strategyTransactionErrors, setStrategyTransactionErrors] = useState({});

  // Strategies state
  const [strategies, setStrategies] = useState([]);
  const [isLoadingStrategies, setIsLoadingStrategies] = useState(false);
  const [strategiesError, setStrategiesError] = useState("");
  const [isAddStrategyModalOpen, setIsAddStrategyModalOpen] = useState(false);
  const [newStrategyName, setNewStrategyName] = useState("");
  const [addStrategyError, setAddStrategyError] = useState("");
  const [isAddingStrategy, setIsAddingStrategy] = useState(false);

  // Segregate Trades state
  const [segregateTradesData, setSegregateTradesData] = useState([]);
  const [segregateTradesColumns, setSegregateTradesColumns] = useState([]);
  const [selectedTrades, setSelectedTrades] = useState([]);
  const [isLoadingSegregate, setIsLoadingSegregate] = useState(false);
  const [errorSegregate, setErrorSegregate] = useState("");
  const [dateFilter, setDateFilter] = useState("last_month");
  const [strategyFilter, setStrategyFilter] = useState("all");
  const [availableStrategies, setAvailableStrategies] = useState(["All Strategies", "Straddle", "Strangle", "Iron Condor"]);
  const [isSegregateTradesModalOpen, setIsSegregateTradesModalOpen] = useState(false);
  const fileInputRef = useRef(null);

  const handlePrimaryTabChange = useCallback((event, newValue) => {
    if (newValue === "batch_management") {
      setHasVisitedBatchTab(true);
    }
    startTransition(() => {
      setPrimaryTab(newValue);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("primaryTab", newValue);
      setSearchParams(newSearchParams, { replace: true });
    });
  }, [searchParams, setSearchParams]);

  const toggleRowExpand = useCallback((id) => {
    setExpandedRows((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(id)) {
        newExpanded.delete(id);
      } else {
        newExpanded.add(id);
        // Initialize date filter if not exists
        if (!strategyDateFilter[id]) {
          setStrategyDateFilter((prev) => ({
            ...prev,
            [id]: "last_month",
          }));
        }
      }
      return newExpanded;
    });
  }, [strategyDateFilter]);

  const fetchStrategies = useCallback(async () => {
    setIsLoadingStrategies(true);
    setStrategiesError("");
    try {
      const response = await getSegregatedStrategies();
      const strategiesData = response?.data || [];

      const filteredStrategies = strategiesData.filter((s) => s.strategy_id != null);
      setStrategies(filteredStrategies);

      const strategyNames = filteredStrategies
        .filter((s) => s.strategy_name)
        .map((s) => s.strategy_name);
      setAvailableStrategies(["All Strategies", ...strategyNames]);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      console.error("Failed to fetch strategies:", errorMessage);
      setStrategiesError(errorMessage);
      setStrategies([]);
    } finally {
      setIsLoadingStrategies(false);
    }
  }, []);

  const closeAddStrategyModal = useCallback(() => {
    if (isAddingStrategy) return;
    setIsAddStrategyModalOpen(false);
    setNewStrategyName("");
    setAddStrategyError("");
    setIsAddingStrategy(false);
  }, [isAddingStrategy]);

  const handleAddStrategy = useCallback(() => {
    setNewStrategyName("");
    setAddStrategyError("");
    setIsAddStrategyModalOpen(true);
  }, []);

  const handleAddStrategySubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const trimmed = newStrategyName.trim();
      if (!trimmed) {
        setAddStrategyError("Enter a strategy name.");
        return;
      }

      setIsAddingStrategy(true);
      setAddStrategyError("");

      try {
        await createStrategy(trimmed);
        setIsAddStrategyModalOpen(false);
        setNewStrategyName("");
        fetchStrategies();
      } catch (err) {
        const message =
          err?.response?.data?.detail ||
          err?.response?.data?.strategy_name?.[0] ||
          err?.message ||
          "Could not create strategy.";
        setAddStrategyError(typeof message === "string" ? message : "Could not create strategy.");
      } finally {
        setIsAddingStrategy(false);
      }
    },
    [newStrategyName, fetchStrategies]
  );

  const handleStrategyReport = useCallback((strategyId) => {
    if (!strategyId) return;
    navigate(`/strategy-report/${strategyId}`);
  }, [navigate]);

  const handleDownloadReport = useCallback((strategyId = null) => {
    console.log("Download Report clicked for:", strategyId || "All Strategies");
  }, []);

  const handleGenerateReport = useCallback(() => {
    console.log("Generate Report clicked");
  }, []);

  const handleGoToSegregateTrades = useCallback(() => {
    setIsSegregateTradesModalOpen(true);
  }, []);

  const handleCloseSegregateTradesModal = useCallback(() => {
    setIsSegregateTradesModalOpen(false);
  }, []);

  useEffect(() => {
    if (primaryTab !== "segregate_trades" || segregateTradesColumns.length > 0) return;

    const fetchColumns = async () => {
      try {
        const response = await getSegregateTradesColumns();
        const columns = extractColumnsConfig(response);
        if (columns?.length > 0) {
          setSegregateTradesColumns(columns);
          setErrorSegregate("");
        } else {
          setErrorSegregate("Failed to fetch columns configuration: No columns returned");
          setSegregateTradesColumns([]);
        }
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        console.error("Failed to fetch columns:", errorMessage);
        setErrorSegregate(errorMessage);
        setSegregateTradesColumns([]);
      }
    };

    fetchColumns();
  }, [primaryTab, segregateTradesColumns.length]);

  useEffect(() => {
    if (primaryTab !== "segregate_trades") return;

    const fetchTrades = async () => {
      setIsLoadingSegregate(true);
      setErrorSegregate("");
      try {
        const response = await getSegregateTrades(buildFilters(dateFilter, strategyFilter));
        const data = normalizeApiData(response);
        setSegregateTradesData(Array.isArray(data) ? data : []);
        if (Array.isArray(data) && data.length === 0) {
          setErrorSegregate("No trades found");
        }
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        console.error("Failed to fetch trades:", errorMessage);
        setErrorSegregate(errorMessage);
        setSegregateTradesData([]);
      } finally {
        setIsLoadingSegregate(false);
      }
    };

    fetchTrades();
  }, [primaryTab, dateFilter, strategyFilter]);

  const handleDateFilterChange = useCallback(async (newValue) => {
    setDateFilter(newValue);
    setErrorSegregate("");
    try {
      const response = await getSegregateTrades(buildFilters(newValue, strategyFilter));
      const data = normalizeApiData(response);
      setSegregateTradesData(Array.isArray(data) ? data : []);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      console.error("Failed to fetch trades:", errorMessage);
      setErrorSegregate(errorMessage);
    }
  }, [strategyFilter]);

  const handleStrategyFilterChange = useCallback(async (newValue) => {
    setStrategyFilter(newValue);
    setErrorSegregate("");
    try {
      const response = await getSegregateTrades(buildFilters(dateFilter, newValue));
      const data = normalizeApiData(response);
      setSegregateTradesData(Array.isArray(data) ? data : []);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      console.error("Failed to fetch trades:", errorMessage);
      setErrorSegregate(errorMessage);
    }
  }, [dateFilter]);

  // Handle file upload
  const handleUploadTrades = () => {
    fileInputRef.current?.click();
  };

  const refreshSegregateData = useCallback(async () => {
    try {
      const response = await getSegregateTrades(buildFilters(dateFilter, strategyFilter));
      const data = normalizeApiData(response);
      setSegregateTradesData(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to refresh data:", getErrorMessage(err));
    }
  }, [dateFilter, strategyFilter]);

  const handleSegregateTradesModalSuccess = useCallback(async () => {
    setSelectedTrades([]);
    await refreshSegregateData();
  }, [refreshSegregateData]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsLoadingSegregate(true);
    setErrorSegregate("");
    try {
      const response = await uploadTradesFile({ trades_file: file });
      if (response?.error_rows?.length) {
        setErrorSegregate(response.error_rows.map((x) => x.detail).join(", "));
      } else {
        await refreshSegregateData();
      }
    } catch (err) {
      setErrorSegregate(getErrorMessage(err));
    } finally {
      setIsLoadingSegregate(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleSegregateTrades = () => {
    setIsSegregateTradesModalOpen(true);
  };

  // Handle row selection
  const handleRowSelect = useCallback((selectedRowIds) => {
    setSelectedTrades(selectedRowIds);
  }, []);

  // Handle strategy transaction row selection
  const handleStrategyTransactionSelect = useCallback((strategyId) => {
    return (selectedRowIds) => {
      console.log(`Strategy ${strategyId} transactions selected:`, selectedRowIds);
    };
  }, []);

  // Handle strategy date filter change
  const handleStrategyDateFilterChange = useCallback((strategyId) => {
    return (newValue) => {
      setStrategyDateFilter((prev) => ({
        ...prev,
        [strategyId]: newValue,
      }));
    };
  }, []);

  useEffect(() => {
    const fetchStrategyColumns = async (strategyId) => {
      if (!expandedRows.has(strategyId) || strategyTransactionColumns[strategyId]) return;
      
      try {
        const response = await getStrategyTransactionsColumns();
        const columns = extractColumnsConfig(response);
        if (columns?.length > 0) {
          setStrategyTransactionColumns((prev) => ({
            ...prev,
            [strategyId]: columns,
          }));
          setStrategyTransactionErrors((prev) => {
            const updated = { ...prev };
            delete updated[strategyId];
            return updated;
          });
        } else {
          const errorMessage = "Failed to fetch columns configuration: No columns returned";
          setStrategyTransactionErrors((prev) => ({
            ...prev,
            [strategyId]: errorMessage,
          }));
          setStrategyTransactionColumns((prev) => ({
            ...prev,
            [strategyId]: [],
          }));
        }
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        console.error(`Failed to fetch columns for strategy ${strategyId}:`, errorMessage);
        setStrategyTransactionErrors((prev) => ({
          ...prev,
          [strategyId]: errorMessage,
        }));
        setStrategyTransactionColumns((prev) => ({
          ...prev,
          [strategyId]: [],
        }));
      }
    };

    expandedRows.forEach((strategyId) => {
      fetchStrategyColumns(strategyId);
    });
  }, [expandedRows, strategyTransactionColumns]);

  useEffect(() => {
    const fetchStrategyTransactions = async (strategyId) => {
      if (!expandedRows.has(strategyId)) return;

      setStrategyTransactionLoading((prev) => ({
        ...prev,
        [strategyId]: true,
      }));
      setStrategyTransactionErrors((prev) => {
        const updated = { ...prev };
        delete updated[strategyId];
        return updated;
      });

      try {
        const response = await getStrategyTransactions(strategyId);
        const data = normalizeApiData(response);
        setStrategyTransactions((prev) => ({
          ...prev,
          [strategyId]: Array.isArray(data) ? data : [],
        }));
        if (Array.isArray(data) && data.length === 0) {
          setStrategyTransactionErrors((prev) => ({
            ...prev,
            [strategyId]: "No transactions found",
          }));
        }
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        console.error(`Failed to fetch transactions for strategy ${strategyId}:`, errorMessage);
        setStrategyTransactionErrors((prev) => ({
          ...prev,
          [strategyId]: errorMessage,
        }));
        setStrategyTransactions((prev) => ({
          ...prev,
          [strategyId]: [],
        }));
      } finally {
        setStrategyTransactionLoading((prev) => ({
          ...prev,
          [strategyId]: false,
        }));
      }
    };

    expandedRows.forEach((strategyId) => {
      fetchStrategyTransactions(strategyId);
    });
  }, [expandedRows]);

  useEffect(() => {
    if (primaryTab !== "my_strategies") return;
    fetchStrategies();
  }, [primaryTab, fetchStrategies]);

  // Calculate overall P&L - memoized to avoid recalculation on every render
  const overallPnL = useMemo(() => {
    return strategies.reduce((sum, strategy) => sum + (strategy.pnl || 0), 0);
  }, [strategies]);

  const isOverallProfit = overallPnL >= 0;

  // Memoize strategy filter options to avoid recreating array on every render
  const strategyFilterOptions = useMemo(() => {
    return [
      { label: "All Strategies", value: "all" },
      ...availableStrategies.slice(1).map((strategy) => ({
        label: strategy,
        value: strategy.toLowerCase().replace(/\s+/g, "_"),
      })),
    ];
  }, [availableStrategies]);

  return (
    <div className={styles["container"]}>
      {/* Primary Tabs */}
      <div className={styles["primary-tabs-container"]}>
        <Tabs
          value={primaryTab}
          onChange={handlePrimaryTabChange}
          className={styles["primary-tabs"]}
        >
          <Tab
            icon={
              <GlobalIcon
                iconName="ChartIcon"
                className={styles["tab-icon"]}
              />
            }
            iconPosition="start"
            label="My Strategies"
            value="my_strategies"
            className={styles["primary-tab"]}
            disableRipple
          />
          <Tab
            icon={
              <GlobalIcon
                iconName="PeopleIcon"
                className={styles["tab-icon"]}
              />
            }
            iconPosition="start"
            label="Segregate Trades"
            value="segregate_trades"
            className={styles["primary-tab"]}
            disableRipple
          />
          <Tab
            icon={
              <GlobalIcon
                iconName="DocumentIcon"
                className={styles["tab-icon"]}
              />
            }
            iconPosition="start"
            label="Batch Management"
            value="batch_management"
            className={styles["primary-tab"]}
            disableRipple
          />
        </Tabs>
      </div>

      <div className={styles["content-wrapper"]}>
        <div className={styles["tab-content"]}>
          {primaryTab === "my_strategies" && (
            <div className={styles["my-strategies-content"]}>
              <div className={styles["summary-row"]}>
                <div className={styles["summary-section"]}>
                  <div className={styles["summary-left"]}>
                    <h2 className={styles["summary-title"]}>All Strategies Performance</h2>
                    <span className={styles["summary-amount"]}>{formatCurrency(overallPnL)}</span>
                    <div className={styles["pnl-indicator"]}>
                      {isOverallProfit ? (
                        <GlobalIcon
                          iconName="ProfitArrowIcon"
                          className={styles["pnl-arrow"]}
                        />
                      ) : (
                        <GlobalIcon
                          iconName="LossArrowIcon"
                          className={styles["pnl-arrow"]}
                        />
                      )}
                      <span
                        className={`${styles["pnl-label"]} ${
                          isOverallProfit ? styles["profit"] : styles["loss"]
                        }`}
                      >
                        Profit & Loss
                      </span>
                    </div>
                  </div>
                  <div className={styles["summary-chart-slot"]}>
                    <SummaryPerformanceChart
                      strategies={strategies}
                      isPositive={isOverallProfit}
                    />
                  </div>
                  <Button
                    variant="outlined"
                    className={styles["download-report-button"]}
                    onClick={() => handleDownloadReport()}
                    startIcon={<GlobalIcon iconName="DownloadIcon" />}
                  >
                    Download Report
                  </Button>
                </div>
                <div className={styles["summary-actions"]}>
                  <Button
                    variant="contained"
                    className={styles["add-strategy-button"]}
                    onClick={handleAddStrategy}
                    fullWidth
                  >
                    + Add Strategy
                  </Button>
                  <Button
                    variant="outlined"
                    className={styles["outline-action-button"]}
                    onClick={handleGenerateReport}
                    fullWidth
                  >
                    Generate Report
                  </Button>
                  <Button
                    variant="outlined"
                    className={styles["outline-action-button"]}
                    onClick={handleGoToSegregateTrades}
                    fullWidth
                  >
                    Segregate Trades
                  </Button>
                </div>
              </div>

              <div className={styles["strategies-section"]}>
                {strategiesError && (
                  <div className={styles["error-message"]}>
                    {strategiesError}
                  </div>
                )}
                {isLoadingStrategies && strategies.length === 0 ? (
                  <div className={styles["loading-message"]}>Loading strategies...</div>
                ) : (
                  <div className={styles["strategies-list"]}>
                    {strategies.length === 0 ? (
                      <div className={styles["loading-message"]}>No strategies found</div>
                    ) : (
                      strategies.map((strategy, index) => {
                        const isProfit = (strategy.pnl || 0) >= 0;
                        const isExpanded = expandedRows.has(strategy.strategy_id);
                        const iconColor = STRATEGY_ICON_COLORS[index % STRATEGY_ICON_COLORS.length];
                        return (
                          <div
                            key={strategy.strategy_id}
                            className={`${styles["strategy-row"]} ${
                              isExpanded ? styles["strategy-row-expanded"] : ""
                            }`}
                          >
                            <div
                              className={styles["strategy-row-content"]}
                              role="button"
                              tabIndex={0}
                              aria-expanded={isExpanded}
                              onClick={() => toggleRowExpand(strategy.strategy_id)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  toggleRowExpand(strategy.strategy_id);
                                }
                              }}
                            >
                              <div className={styles["strategy-identity"]}>
                                <span
                                  className={styles["strategy-icon-dot"]}
                                  style={{ backgroundColor: iconColor }}
                                  aria-hidden
                                />
                                <div className={styles["strategy-name"]} title={strategy.strategy_name}>
                                  {strategy.strategy_name}
                                </div>
                              </div>

                              <div className={styles["strategy-metrics-group"]}>
                              <div className={styles["strategy-pnl-block"]}>
                                <span className={styles["strategy-pnl-amount"]}>
                                  {formatCurrency(strategy.pnl || 0)}
                                </span>
                                <div className={styles["strategy-pnl-indicator"]}>
                                  {isProfit ? (
                                    <GlobalIcon
                                      iconName="ProfitArrowIcon"
                                      className={styles["strategy-pnl-arrow"]}
                                    />
                                  ) : (
                                    <GlobalIcon
                                      iconName="LossArrowIcon"
                                      className={styles["strategy-pnl-arrow"]}
                                    />
                                  )}
                                  <span
                                    className={`${styles["strategy-pnl-label"]} ${
                                      isProfit ? styles["profit"] : styles["loss"]
                                    }`}
                                  >
                                    Profit & Loss
                                  </span>
                                </div>
                              </div>

                              <div className={styles["strategy-trades-block"]}>
                                <span className={styles["strategy-trades-count"]}>
                                  {(strategy.trade_count || 0).toLocaleString("en-IN")}
                                </span>
                                <span className={styles["strategy-trades-label"]}>Total Trades</span>
                              </div>
                              </div>

                              <div className={styles["strategy-actions-block"]}>
                                <span
                                  className={styles["strategy-report-link"]}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleStrategyReport(strategy.strategy_id);
                                  }}
                                >
                                  Strategy Report
                                </span>
                                <Button
                                  variant="outlined"
                                  className={styles["download-report-button-small"]}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDownloadReport(strategy.strategy_id);
                                  }}
                                  startIcon={<GlobalIcon iconName="DownloadIcon" />}
                                >
                                  Download Report
                                </Button>
                                <span
                                  className={styles["expand-button"]}
                                  aria-hidden="true"
                                >
                                  <GlobalIcon
                                    iconName="ChevronDownIcon"
                                    className={`${styles["chevron-icon"]} ${
                                      isExpanded ? styles["expanded"] : ""
                                    }`}
                                  />
                                </span>
                              </div>
                            </div>
                            <div 
                              className={`${styles["strategy-expanded-content"]} ${
                                isExpanded ? styles["expanded"] : styles["collapsed"]
                              }`}
                            >
                              {/* Error Display */}
                              {strategyTransactionErrors[strategy.strategy_id] && (
                                <div className={styles["error-message"]}>
                                  {strategyTransactionErrors[strategy.strategy_id]}
                                </div>
                              )}
                              <div className={styles["strategy-table-wrapper"]}>
                                {strategyTransactionLoading[strategy.strategy_id] && 
                                 (!strategyTransactions[strategy.strategy_id] || strategyTransactions[strategy.strategy_id].length === 0) ? (
                                  <div className={styles["loading-message"]}>Loading...</div>
                                ) : (
                                  <CustomTable
                                    columns={strategyTransactionColumns[strategy.strategy_id] || []}
                                    data={strategyTransactions[strategy.strategy_id] || []}
                                    onRowSelect={handleStrategyTransactionSelect(strategy.strategy_id)}
                                    selectedRows={[]}
                                    rowKey="id"
                                    enableSearch={false}
                                    enableSorting={true}
                                    enableSelection={true}
                                    enableShowEntries={true}
                                    enableFilter={false}
                                    enableExport={false}
                                    enablePagination={true}
                                    defaultEntriesPerPage={25}
                                    columnsWithDropdown={COLUMNS_WITH_DROPDOWN}
                                    enableDateFilter={true}
                                    dateFilterOptions={DATE_FILTER_OPTIONS}
                                    dateFilterValue={strategyDateFilter[strategy.strategy_id] || "last_month"}
                                    onDateFilterChange={handleStrategyDateFilterChange(strategy.strategy_id)}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {primaryTab === "segregate_trades" && (
            <>
              {/* Action Buttons */}
              <div className={styles["action-buttons-container"]}>
                <Button
                  variant="contained"
                  className={styles["upload-trades-button"]}
                  onClick={handleUploadTrades}
                >
                  Upload Trades
                </Button>
                <Button
                  variant="contained"
                  className={styles["segregate-trades-button"]}
                  onClick={handleSegregateTrades}
                  disabled={isLoadingSegregate}
                >
                  Segregate Trades
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>

              {/* Error Display */}
              {errorSegregate && (
                <div className={styles["error-message"]}>
                  {errorSegregate}
                </div>
              )}

              {/* Table */}
              <div className={styles["table-wrapper"]}>
                {isLoadingSegregate && segregateTradesData.length === 0 && segregateTradesColumns.length === 0 ? (
                  <div className={styles["loading-message"]}>Loading...</div>
                ) : segregateTradesColumns.length === 0 ? (
                  <div className={styles["error-message"]}>
                    {errorSegregate || "Failed to load table columns"}
                  </div>
                ) : (
                  <CustomTable
                    columns={segregateTradesColumns}
                    data={segregateTradesData}
                    onRowSelect={handleRowSelect}
                    selectedRows={selectedTrades}
                    rowKey="id"
                    enableSearch={false}
                    enableSorting={true}
                    enableSelection={true}
                    enableShowEntries={true}
                    enableFilter={false}
                    enableExport={false}
                    enablePagination={true}
                    defaultEntriesPerPage={25}
                    columnsWithDropdown={COLUMNS_WITH_DROPDOWN}
                    enableDateFilter={true}
                    dateFilterOptions={DATE_FILTER_OPTIONS}
                    dateFilterValue={dateFilter}
                    onDateFilterChange={handleDateFilterChange}
                    enableStrategyFilter={true}
                    strategyFilterOptions={strategyFilterOptions}
                    strategyFilterValue={strategyFilter}
                    onStrategyFilterChange={handleStrategyFilterChange}
                  />
                )}
              </div>
            </>
          )}

          {hasVisitedBatchTab && (
            <div
              className={primaryTab === "batch_management" ? undefined : styles["tab-panel-hidden"]}
              aria-hidden={primaryTab !== "batch_management"}
            >
              <BatchManagementTab />
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={isAddStrategyModalOpen}
        onClose={closeAddStrategyModal}
        maxWidth={false}
        PaperProps={{ className: styles["add-strategy-dialog"] }}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "rgba(15, 23, 42, 0.4)" },
          },
        }}
      >
        <form className={styles["add-strategy-form"]} onSubmit={handleAddStrategySubmit} noValidate>
          <div className={styles["add-strategy-header"]}>
            <div className={styles["add-strategy-header-icon"]}>
              <GlobalIcon iconName="StrategyProfilingIcon" className={styles["add-strategy-icon"]} />
            </div>
            <div className={styles["add-strategy-header-text"]}>
              <h2 className={styles["add-strategy-title"]}>Add strategy</h2>
              <p className={styles["add-strategy-subtitle"]}>Create a new strategy for profiling and reports.</p>
            </div>
            <IconButton
              type="button"
              onClick={closeAddStrategyModal}
              disabled={isAddingStrategy}
              className={styles["add-strategy-close"]}
              aria-label="Close"
              size="small"
            >
              <GlobalIcon iconName="RoundedCrossIcon" className={styles["add-strategy-close-icon"]} />
            </IconButton>
          </div>

          <div className={styles["add-strategy-body"]}>
            <TextField
              label="Strategy name"
              placeholder="e.g. Strangle, Iron Condor"
              value={newStrategyName}
              onChange={(e) => {
                setNewStrategyName(e.target.value);
                if (addStrategyError) setAddStrategyError("");
              }}
              fullWidth
              size="small"
              autoFocus
              disabled={isAddingStrategy}
              inputProps={{ maxLength: 120 }}
              className={styles["add-strategy-field"]}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <GlobalIcon iconName="DocumentIcon" className={styles["add-strategy-field-icon"]} />
                  </InputAdornment>
                ),
              }}
            />
            {addStrategyError ? (
              <Alert severity="error" className={styles["add-strategy-alert"]}>
                {addStrategyError}
              </Alert>
            ) : null}
          </div>

          <div className={styles["add-strategy-actions"]}>
            <Button
              type="button"
              onClick={closeAddStrategyModal}
              disabled={isAddingStrategy}
              className={styles["add-strategy-cancel"]}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!newStrategyName.trim() || isAddingStrategy}
              className={styles["add-strategy-submit"]}
              startIcon={
                isAddingStrategy ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <GlobalIcon iconName="AddIcon" className={styles["add-strategy-submit-icon"]} />
                )
              }
            >
              {isAddingStrategy ? "Adding…" : "Add strategy"}
            </Button>
          </div>
        </form>
      </Dialog>

      <SegregateTradesModal
        open={isSegregateTradesModalOpen}
        onClose={handleCloseSegregateTradesModal}
        onSuccess={handleSegregateTradesModalSuccess}
        initialSelectedIds={selectedTrades}
      />
    </div>
  );
}

export default StrategyProfilingPage;

